import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import axios from "../axiosInstance";
import { useCommonVars } from "../components/useCommonVars";
import "../pages/crypto-deposit.css";

const ids = { USDT: "tether", TON: "the-open-network", TRX: "tron", BTC: "bitcoin" };

export default function CryptoDepositPage({ user, logo }) {
  const navigate = useNavigate();
  const location = useLocation();
  const initialRUB = useMemo(() => {
    const v = Number(location.state?.amount || "");
    return Number.isFinite(v) && v > 0 ? String(v) : "";
  }, [location.state]);
  const { cryptoWallets, minInvest } = useCommonVars(user);
  const [rubAmount, setRubAmount] = useState(initialRUB);
  const [selected, setSelected] = useState("USDT");
  const [rates, setRates] = useState({});
  const [cryptoAmount, setCryptoAmount] = useState("0.00");
  const [toast, setToast] = useState("");
  const [copyOk, setCopyOk] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const copyTimeout = useRef();

  const options = {
    USDT: { address: cryptoWallets.USDT_TRON, label: "USDT (TRC-20)", short: "USDT" },
    USDT_BEP20: { address: cryptoWallets.USDT_BEP, label: "USDT (BEP-20)", short: "USDT" },
    TON: { address: cryptoWallets.TON, label: "TON", short: "TON" },
    TRX: { address: cryptoWallets.TRON, label: "TRX", short: "TRX" },
    BTC: { address: cryptoWallets.BTC, label: "BTC", short: "BTC" }
  };

  useEffect(() => {
    const key = selected === "USDT_BEP20" ? "USDT" : selected;
    if (!rubAmount || !rates[key]) {
      setCryptoAmount("0.00");
      return;
    }
    const decimals = key === "BTC" ? 6 : 2;
    const v = (parseFloat(rubAmount) / rates[key]).toFixed(decimals);
    setCryptoAmount(v);
  }, [rubAmount, selected, rates]);

  useEffect(() => {
    const saved = localStorage.getItem("cryptoRates");
    const update = async () => {
      try {
        const q = `${ids.USDT},${ids.TON},${ids.TRX},${ids.BTC}`;
        const r = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${q}&vs_currencies=rub`);
        const d = await r.json();
        const rs = { USDT: d[ids.USDT]?.rub, TON: d[ids.TON]?.rub, TRX: d[ids.TRX]?.rub, BTC: d[ids.BTC]?.rub };
        setRates(rs);
        localStorage.setItem("cryptoRates", JSON.stringify({ rates: rs, updated: Date.now() }));
      } catch {}
    };
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Date.now() - (parsed.updated || 0) > 3600000) update();
        else setRates(parsed.rates || {});
      } catch {
        update();
      }
    } else update();
  }, []);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(""), 2200);
      return () => clearTimeout(t);
    }
  }, [toast]);

  useEffect(() => {
    document.documentElement.classList.add("av-hidden");
    return () => document.documentElement.classList.remove("av-hidden");
  }, []);

  const addr = options[selected]?.address || "";
  const canSubmit = Number(rubAmount) >= Number(minInvest) && addr;

  const showCopied = (key) => {
    setCopyOk(key);
    setToast("Скопировано");
    clearTimeout(copyTimeout.current);
    copyTimeout.current = setTimeout(() => setCopyOk(""), 900);
  };

  const copyText = async (value, key) => {
    if (!value) return;
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        ok = true;
      }
    } catch {}
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        ok = true;
      } catch {}
    }
    if (ok) showCopied(key);
    else setToast("Не удалось скопировать");
  };

  const submit = async () => {
    if (!canSubmit) {
      setToast(`Минимальная сумма: ${minInvest}₽`);
      return;
    }
    setSubmitting(true);
    try {
      const key = selected === "USDT_BEP20" ? "USDT" : selected;
      const payload = {
        user_id: user?.telegram_id,
        pay_id: "-",
        method: selected,
        amount: rubAmount,
        crypto_amount: cryptoAmount !== "0.00" ? cryptoAmount : undefined,
        crypto_code: options[selected]?.short || key
      };
      const res = await axios.post("/project/send-payment-message", payload, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (res.data?.status === "ok") {
        navigate("/card", {
          replace: true,
          state: { confirmMessage: "✅ Заявка отправлена. Ожидайте подтверждения администратора." }
        });
      } else {
        setToast("Не удалось отправить заявку");
      }
    } catch {
      setToast("Ошибка при подтверждении");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cdp-page">
      {toast && <div className="cdp-toast">{toast}</div>}
      <div className="cdp-card">
        <div className="cdp-head">
          <div className="cdp-title">Криптовалютный перевод</div>
          <div className="cdp-sub">Переведите точную сумму на выбранную сеть</div>
        </div>

        <div className="cdp-amount">
          <label htmlFor="amount" className="cdp-amount-label">Сумма, ₽</label>
          <div className="cdp-amount-wrap">
            <input
              id="amount"
              className="cdp-amount-input"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder={`${minInvest} и выше`}
              value={rubAmount}
              onChange={(e) => setRubAmount(e.target.value.replace(/[^\d]/g, ""))}
            />
            {rubAmount ? (
              <button className="cdp-iconbtn cdp-clear" aria-label="Очистить" onClick={() => setRubAmount("")}>
                <svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6l-12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
              </button>
            ) : null}
            <button
              className={`cdp-iconbtn cdp-copyin ${copyOk==="rub"?"cdp-active":""}`}
              aria-label="Скопировать сумму в ₽"
              onClick={() => copyText(rubAmount, "rub")}
              disabled={!rubAmount}
            >
              <svg viewBox="0 0 24 24"><path d="M9 9h9a2 2 0 012 2v7a2 2 0 01-2 2H9a2 2 0 01-2-2v-7a2 2 0 012-2z"/><path d="M7 9V7a2 2 0 012-2h7" stroke="currentColor" strokeWidth="2" fill="none"/></svg>
            </button>
          </div>
          <div className="cdp-amount-hint">Минимум {minInvest}₽</div>
          <div className="cdp-quick">
            {[500, 1000, 3000, 5000].map(v => (
              <button key={v} className="cdp-quick-btn" onClick={() => setRubAmount(String((Number(rubAmount||0)+v)))}>+{v}</button>
            ))}
          </div>
        </div>

        <div className="cdp-networks">
          <button className={`cdp-net ${selected==="USDT"?"cdp-active":""}`} onClick={() => setSelected("USDT")}>
            <span className="cdp-badge">USDT</span><span className="cdp-tag">TRC-20</span>
          </button>
          <button className={`cdp-net ${selected==="USDT_BEP20"?"cdp-active":""}`} onClick={() => setSelected("USDT_BEP20")}>
            <span className="cdp-badge">USDT</span><span className="cdp-tag">BEP-20</span>
          </button>
          <button className={`cdp-net ${selected==="TON"?"cdp-active":""}`} onClick={() => setSelected("TON")}>
            <span className="cdp-badge">TON</span><span className="cdp-tag">Toncoin</span>
          </button>
          <button className={`cdp-net ${selected==="TRX"?"cdp-active":""}`} onClick={() => setSelected("TRX")}>
            <span className="cdp-badge">TRX</span><span className="cdp-tag">Tron</span>
          </button>
          <button className={`cdp-net ${selected==="BTC"?"cdp-active":""}`} onClick={() => setSelected("BTC")}>
            <span className="cdp-badge">BTC</span><span className="cdp-tag">Bitcoin</span>
          </button>
        </div>

        <div className="cdp-conv">
          <div className="cdp-conv-row">
            <div className="cdp-conv-label">К переводу</div>
            <div className="cdp-conv-value">
              ~{cryptoAmount} {options[selected]?.short}
              <button
                className={`cdp-mini-copy ${copyOk==="crypto"?"cdp-active":""}`}
                onClick={() => copyText(cryptoAmount, "crypto")}
                disabled={cryptoAmount==="0.00"}
              >
                <svg viewBox="0 0 24 24"><path d="M9 9h9a2 2 0 012 2v7a2 2 0 01-2 2H9a2 2 0 01-2-2v-7a2 2 0 012-2z"/><path d="M7 9V7a2 2 0 012-2h7" stroke="currentColor" strokeWidth="2" fill="none"/></svg>
              </button>
            </div>
          </div>
          <div className="cdp-conv-note">Курс обновляется автоматически. Переводите только {options[selected]?.label}.</div>
        </div>

        <div className="cdp-qrgrid">
          <div className="cdp-qrcard">
            <div className="cdp-qrpad">
              <QRCodeCanvas
                value={addr || "-"}
                size={196}
                bgColor="#ffffff"
                fgColor="#111827"
                level="H"
                includeMargin
              />
              <div className="cdp-qrlogo">
                {logo ? <img alt="" src={logo}/> : <span className="cdp-spot"/>}
              </div>
            </div>
          </div>
          <div className="cdp-addr">
            <div className="cdp-addr-title">Адрес {options[selected]?.label}</div>
            <div className="cdp-addr-row">
              <textarea
                className="cdp-addr-text"
                readOnly
                value={addr}
                onClick={() => copyText(addr, "addr")}
              />
              <button className={`cdp-copy ${copyOk==="addr"?"cdp-active":""}`} onClick={() => copyText(addr, "addr")}>Копировать</button>
            </div>
            <div className="cdp-chips">
              <span className="cdp-chip">{options[selected]?.label}</span>
              <span className="cdp-chip">{cryptoAmount} {options[selected]?.short}</span>
            </div>
          </div>
        </div>

        <div className="cdp-actions">
          <button className="cdp-btn cdp-ghost" onClick={() => navigate(-1)}>Назад</button>
          <button className="cdp-btn cdp-primary" disabled={!canSubmit || submitting} onClick={submit}>
            {submitting ? "Отправка..." : "Подтвердить"}
          </button>
        </div>
      </div>
    </div>
  );
}

